import React, { useContext } from "react";
import axios from "axios";
import { AUTHORIZATION_HEADER } from "../../../constants";
import { encode } from "html-entities";

const EdgeApiContext = React.createContext(axios);
const useEdgeApiContext = () => useContext(EdgeApiContext);

const defaultHeaders = {
  "Content-Type": "application/json"
};
export const encodedData = (requestBody, isPost) => {
  const emailField = isPost ? "email" : "primaryEmail";
  if (requestBody.isCoppa) {
    const { givenName, userName, ...rest } = requestBody;
    const encodedGivenName = encode(givenName);
    const encodedUserName = encode(userName);
    return {
      givenName: encodedGivenName,
      userName: encodedUserName,
      ...rest
    };
  } else {
    const { givenName, familyName, ...rest } = requestBody;
    const encodedGivenName = encode(givenName);
    const encodedFamilyName = encode(familyName);
    const encodedEmail = encode(requestBody[emailField]);
    return {
      givenName: encodedGivenName,
      familyName: encodedFamilyName,
      [emailField]: encodedEmail,
      ...rest
    };
  }
};
const EdgeApiProvider = ({ baseUrl, publicBaseUrl, headers, ...props }) => {
  const personalUserApi = {
    putPassword: payload =>
      axios.put(`${baseUrl}/personaluser/password`, payload, {
        headers: defaultHeaders
      }),

    putPersonalUser: payload => {
      const encodedPayload = encodedData(payload, false);
      return axios.put(`${baseUrl}/personaluser`, encodedPayload, {
        headers: defaultHeaders
      });
    },

    postPersonalUser: (payload, authToken) => {
      /**
       * Providing CGP and authToken in POST /personaluser request will create new account and auto affiliate it.
       * Excluding CGP and authToken from POST /personaluser request will create new account WITHOUT auto afiliate it.
       * If some but not all CGP and authToken parameters are in POST /personaluser request, the request will FAIL.
       *
       * To avoid stopping the user dead in their tracks when creating a new account through the UI, if any 1 of
       * the CGP or authToken parameters were not provided, exclude all CGP and authToken params.
       *
       * Auth service will then take care of auto affiliation upon login after account creation.
       */
      const { groupId, profileId, customerId, ...rest } = payload;
      let requestBody = rest;
      let authorizationHeader = {};

      if (groupId && profileId && customerId && authToken) {
        requestBody = payload;
        authorizationHeader = {
          [AUTHORIZATION_HEADER]: `Bearer ${authToken}`
        };
      }

      const encodedRequestBody = encodedData(requestBody, true);

      const url = `${publicBaseUrl}/v2/personaluser`;

      return axios.post(url, encodedRequestBody, {
        headers: {
          ...defaultHeaders,
          ...authorizationHeader
        }
      });
    },

    postPersonalUserVerify: username =>
      axios.post(
        `${publicBaseUrl}/personaluser/verify`,
        { username },
        {
          headers: defaultHeaders
        }
      ),

    postVerifyVerificationCode: (verificationCode, username) =>
      axios.post(
        `${publicBaseUrl}/verificationCodes/${verificationCode}/verify`,
        { username },
        {
          headers: defaultHeaders
        }
      ),

    postVerifySecretQuestionAnswer: (secretQuestionAnswer, username) =>
      axios.post(
        `${publicBaseUrl}/secretQuestions/verifyAnswer`,
        {
          username,
          secretQuestionAnswer
        },
        { headers: defaultHeaders }
      ),

    postPassword: (newPassword, verificationData, username) =>
      axios.post(
        `${publicBaseUrl}/personaluser/password`,
        { username, verificationData, newPassword },
        {
          headers: defaultHeaders
        }
      )
  };
  return <EdgeApiContext.Provider {...props} value={personalUserApi} />;
};

export { useEdgeApiContext };
export default EdgeApiProvider;
